import { HocuspocusProvider, HocuspocusProviderWebsocket } from "@hocuspocus/provider";
import { IndexeddbPersistence } from "y-indexeddb";
import * as Y from "yjs";

/**
 * One WebSocket per device, shared by every open document (Hocuspocus multiplexing).
 *
 * Battery rules: no awareness (the server's application ping is the only idle traffic),
 * the socket is torn down entirely after 5 minutes hidden (so no provider timers keep
 * running), and reconnect attempts stop while the browser reports being offline.
 *
 * Never-lose-words rule: every document with local edits the server hasn't
 * acknowledged is recorded as "pending" in localStorage. Pending documents are
 * flushed in the background whenever a connection is available, even if they're
 * no longer open.
 */

export type SyncState = "synced" | "syncing" | "offline";
export type SocketStatus = "connecting" | "connected" | "disconnected";

const HIDDEN_DISCONNECT_MS = 5 * 60 * 1000;

interface Handlers {
  onStateless?(payload: string): void;
  /** Server refused this document (missing, not ours). */
  onDenied?(reason: string): void;
}

interface Entry {
  name: string;
  ydoc: Y.Doc;
  handlers: Handlers;
  provider: HocuspocusProvider | null;
  /** Document id to track as pending, or null for channels (library). */
  pendingId: string | null;
}

export const docStoreName = (userId: string, docId: string) => `quill:${userId}:${docId}`;

export class SyncClient {
  private socket: HocuspocusProviderWebsocket | null = null;
  private entries = new Map<string, Entry>();
  private listeners = new Set<() => void>();
  private hideTimer: ReturnType<typeof setTimeout> | undefined;
  private flushing = new Set<string>();
  socketStatus: SocketStatus = "disconnected";

  constructor(
    private url: string,
    private userId: string,
  ) {
    document.addEventListener("visibilitychange", this.onVisibility);
    window.addEventListener("online", this.onOnline);
    window.addEventListener("offline", this.onOffline);
    this.resume();
  }

  // ---- transport lifecycle ------------------------------------------------

  private get suspended() {
    return this.socket === null;
  }

  resume() {
    if (!this.suspended) return;
    this.socket = new HocuspocusProviderWebsocket({
      url: this.url,
      // The server pings every 45 s; three missed pings means the connection is dead.
      messageReconnectTimeout: 140_000,
      delay: 1000,
      minDelay: 1000,
      maxDelay: 30_000,
      factor: 2,
      maxAttempts: 0,
      jitter: true,
      autoConnect: navigator.onLine,
    });
    // Until the first status event, an online device is connecting, not offline.
    this.socketStatus = navigator.onLine ? "connecting" : "disconnected";
    this.socket.on("status", ({ status }: { status: SocketStatus }) => {
      this.socketStatus = status;
      if (status === "connected") this.flushPending();
      this.emit();
    });
    for (const entry of this.entries.values()) this.createProvider(entry);
    this.emit();
  }

  /** Tears down the socket and all providers. Documents and their local stores stay as they are. */
  suspend() {
    if (this.suspended) return;
    for (const entry of this.entries.values()) {
      entry.provider?.destroy();
      entry.provider = null;
    }
    this.socket!.destroy();
    this.socket = null;
    this.socketStatus = "disconnected";
    this.emit();
  }

  private onVisibility = () => {
    clearTimeout(this.hideTimer);
    if (document.visibilityState === "hidden") {
      this.hideTimer = setTimeout(() => this.suspend(), HIDDEN_DISCONNECT_MS);
    } else {
      this.resume();
    }
  };

  private onOnline = () => {
    this.socket?.connect();
    this.emit();
  };

  private onOffline = () => {
    // Stop reconnect attempts until the browser says we're back.
    this.socket?.disconnect();
    this.emit();
  };

  destroy() {
    clearTimeout(this.hideTimer);
    document.removeEventListener("visibilitychange", this.onVisibility);
    window.removeEventListener("online", this.onOnline);
    window.removeEventListener("offline", this.onOffline);
    this.suspend();
    this.entries.clear();
  }

  // ---- documents ----------------------------------------------------------

  /** Connects a Y.Doc to the server under `name` until the returned function is called. */
  attach(name: string, ydoc: Y.Doc, handlers: Handlers = {}, pendingId: string | null = null): () => void {
    const entry: Entry = { name, ydoc, handlers, provider: null, pendingId };
    this.entries.set(name, entry);
    if (pendingId) {
      ydoc.on("update", (_update: Uint8Array, origin: unknown) => {
        // Anything not coming from the server is a local change the server may not have.
        if (!(origin instanceof HocuspocusProvider)) this.markPending(pendingId);
      });
    }
    if (!this.suspended) this.createProvider(entry);
    this.emit();
    return () => {
      const p = entry.provider;
      this.entries.delete(name);
      p?.destroy();
      this.emit();
    };
  }

  private createProvider(entry: Entry) {
    const provider = new HocuspocusProvider({
      websocketProvider: this.socket!,
      name: entry.name,
      document: entry.ydoc,
      // The session cookie authenticates the socket; the token only triggers the server's ownership check.
      token: "session",
      awareness: null,
      onStateless: ({ payload }) => entry.handlers.onStateless?.(payload),
      onAuthenticationFailed: ({ reason }) => {
        if (entry.pendingId && reason === "not-found") this.clearPending(entry.pendingId);
        entry.handlers.onDenied?.(reason);
      },
      onSynced: () => this.afterSync(entry),
      onUnsyncedChanges: () => this.afterSync(entry),
    });
    provider.attach();
    entry.provider = provider;
  }

  private afterSync(entry: Entry) {
    const p = entry.provider;
    if (entry.pendingId && p?.isSynced && !p.hasUnsyncedChanges) this.clearPending(entry.pendingId);
    this.emit();
  }

  /** Sync state for one document, for the status dot. */
  state(name: string): SyncState {
    const p = this.entries.get(name)?.provider;
    if (!navigator.onLine || this.socketStatus !== "connected" || !p) return "offline";
    return p.isSynced && !p.hasUnsyncedChanges ? "synced" : "syncing";
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    for (const fn of this.listeners) fn();
  }

  // ---- pending (unsynced) documents ---------------------------------------

  private get pendingKey() {
    return `quill.pending.${this.userId}`;
  }

  pending(): string[] {
    try {
      return JSON.parse(localStorage.getItem(this.pendingKey) ?? "[]") as string[];
    } catch {
      return [];
    }
  }

  private writePending(ids: string[]) {
    try {
      localStorage.setItem(this.pendingKey, JSON.stringify(ids));
    } catch {
      // ignore
    }
  }

  private markPending(id: string) {
    const ids = this.pending();
    if (!ids.includes(id)) this.writePending([...ids, id]);
  }

  private clearPending(id: string) {
    const ids = this.pending();
    if (ids.includes(id)) this.writePending(ids.filter((x) => x !== id));
  }

  /** Background-syncs pending documents that aren't open, one at a time. */
  private async flushPending() {
    for (const id of this.pending()) {
      if (this.entries.has(id) || this.flushing.has(id) || this.suspended) continue;
      this.flushing.add(id);
      try {
        await this.flushOne(id);
      } finally {
        this.flushing.delete(id);
      }
    }
  }

  private async flushOne(id: string) {
    const ydoc = new Y.Doc();
    const idb = new IndexeddbPersistence(docStoreName(this.userId, id), ydoc);
    await idb.whenSynced;
    await new Promise<void>((resolve) => {
      let finished = false;
      let detach: (() => void) | undefined;
      const done = () => {
        if (finished) return;
        finished = true;
        clearTimeout(timeout);
        off();
        detach?.();
        resolve();
      };
      const timeout = setTimeout(done, 30_000);
      const off = this.subscribe(() => {
        if (detach && this.state(id) === "synced") done();
      });
      detach = this.attach(id, ydoc, { onDenied: done }, id);
    });
    await idb.destroy();
    ydoc.destroy();
  }
}
